import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { EstimateState } from './estimate.types';

import { EE_STATUS, STATUS_REFRESH } from '~/core/constants';
import { LoadEstimateKeys } from '~/core/hooks/useEstimate';
import { DEFAULT_ESTIMATE, Estimate } from '~/core/models';
import { createEstimateStatus } from '~/core/models/save-estimate-dto';

export const nullEstimateStatus = {
  imeMessage: null,
  imeStatus: null,
  paymentUrl: null,
  status: null,
};

const initialState: EstimateState = {
  currentEstimate: DEFAULT_ESTIMATE,
  eeStatusList: [],
  loadEstimateKeys: undefined,
  isSavingEstimate: false,
  statusUpdateInterval: STATUS_REFRESH.OFF,
  validSmallTicketNumber: undefined,
  discountOverride: undefined,
  ignoreFetchedEstimate: false,
  prevEstimateStatus: createEstimateStatus(),
  completedEstimates: [],
  isCompletedEstimate: false,
  showListOfEstimates: false,
  currentEstimateCode: undefined,
  loadEstimateCode: undefined,
  isNewEstimate: false,
  isWatchingEstimate: false,
  estimateHasBeenSaved: false,
};

const estimateSlice = createSlice({
  name: 'estimate',
  initialState,
  reducers: {
    updateCurrentEstimate: (state, action: PayloadAction<Estimate>) => {
      Object.assign(state, {
        currentEstimate: action.payload,
        currentEstimateCode: action.payload.estimateNumber,
      });
    },
    updateLoadEstimateKeys: (
      state,
      action: PayloadAction<LoadEstimateKeys | undefined>
    ) => {
      state.loadEstimateKeys = action.payload;
    },
    updateIsSavingEstimate: (state, action: PayloadAction<boolean>) => {
      state.isSavingEstimate = action.payload;
    },
    resetSmallProjectTicketNumber: (state) => {
      state.validSmallTicketNumber = initialState.validSmallTicketNumber;
    },
    updateValidSmallTicketNumber: (
      state,
      action: PayloadAction<EstimateState['validSmallTicketNumber']>
    ) => {
      state.validSmallTicketNumber = action.payload;
    },
    updatestatusUpdateInterval: (state, action: PayloadAction<number>) => {
      state.statusUpdateInterval = action.payload;
    },
    updateEeStatusList: (state, action: PayloadAction<EE_STATUS[]>) => {
      state.eeStatusList = action.payload;
    },
    addCompletedEstimate: (
      state,
      action: PayloadAction<{
        code?: string | null;
        isCompletedEstimate: boolean;
      }>
    ) => {
      const { code, isCompletedEstimate } = action.payload;

      if (code && !state.completedEstimates.includes(code)) {
        state.completedEstimates = [...state.completedEstimates, code];
        state.isCompletedEstimate = isCompletedEstimate;
        state.currentEstimateCode = code;
        if (isCompletedEstimate) {
          state.showListOfEstimates = true;
        }
      }
    },
    resetEstimate: (state) => {
      Object.assign(state, initialState);
    },
    updateIsCompletedEstimate: (state, action: PayloadAction<boolean>) => {
      state.isCompletedEstimate = action.payload;
    },
    updateShowListOfEstimates: (state, action: PayloadAction<boolean>) => {
      state.showListOfEstimates = action.payload;
    },
    updateCurrentEstimateCode: (state, action: PayloadAction<string>) => {
      state.currentEstimateCode = action.payload;
    },
    updateLoadEstimateCode: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.loadEstimateCode = action.payload;
    },
    updateIsNewEstimate: (state, action: PayloadAction<boolean>) => {
      state.isNewEstimate = action.payload;
    },
    updateIsWatchingEstimate: (state, action: PayloadAction<boolean>) => {
      state.isWatchingEstimate = action.payload;
    },
    updateEstimateHasBeenSaved: (state, action: PayloadAction<boolean>) => {
      state.estimateHasBeenSaved = action.payload;
    },
  },
});

export default estimateSlice.reducer;
export const {
  updateCurrentEstimate,
  updateLoadEstimateKeys,
  updateIsSavingEstimate,
  updatestatusUpdateInterval,
  updateEeStatusList,
  addCompletedEstimate,
  updateIsCompletedEstimate,
  updateShowListOfEstimates,
  updateCurrentEstimateCode,
  resetEstimate,
  updateLoadEstimateCode,
  updateIsNewEstimate,
  updateIsWatchingEstimate,
  updateEstimateHasBeenSaved,
  resetSmallProjectTicketNumber,
  updateValidSmallTicketNumber,
} = estimateSlice.actions;
